import { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Slab } from 'react-loading-indicators';
import { Context } from '../../Context';
import { ReactComponent as IconHeart } from '../ApartmentView/assets/icons/icon-heart.svg';
import { ReactComponent as IconSale } from '../../media/img/icon-sale.svg';
import { ReactComponent as IconClose } from '../ApartmentView/assets/icons/icon-close.svg';
import styles from '../../styles/bim.module.scss';
import { AVAILABILITY_STATUS } from '../../constants/availabilityStatus';

const { AVAILABLE, SOLD, RESERVED, UNKNOWN } = AVAILABILITY_STATUS;

function ApartmentTile({ apartmentData, showPrice = false }) {
	const {
		dataUrl,
		setActiveApartmentHandle,
		favoriteApartmentUUIDs,
		toggleFavoriteApartmentHandler,
		tiersData,
		buildingsData,
	} = useContext(Context);
	const [svgContent, setSvgContent] = useState(null);
	const [isHeartHovered, setIsHeartHovered] = useState(false);
	const svgRef = useRef(null);
	const { t } = useTranslation();

	const isMultipleEdifice = buildingsData.length !== 1;
	const apartmentEdificeName = apartmentData.building_id;
	const apartmentTier = apartmentData.ref_tier;
	const apartmentTierName = tiersData?.find(
		(tier) =>
			tier.edifice_id === apartmentEdificeName && tier.ref_tier === apartmentTier
	)?.name;

	const isCurrentUuidInFavorites = favoriteApartmentUUIDs.includes(
		apartmentData.uuid
	);

	const fetchSvgContent = async () => {
		try {
			const ref_tier = String(apartmentData.ref_tier).padStart(2, '0');
			const uuid = apartmentData.uuid;
			// const svgPath = `${dataUrl}edifices/${apartmentData.edifice_name}/apartments/${uuid}_${ref_tier}.svg`;
			const svgPath = `${dataUrl}apartments/${uuid}/${uuid}_${apartmentData.building_id}-00-${ref_tier}.svg`;


			const response = await fetch(svgPath);
			if (!response.ok)
				throw new Error(`Failed to fetch SVG: ${response.statusText}`);

			const blob = await response.blob();
			const svgText = await blob.text();
			// const defsPath = `${dataUrl}edifices/${apartmentData.edifice_name}/apartments/defs.svg`;
			const defsPath = `${dataUrl}apartments/defs.svg`;


			try {
				const modifiedText = svgText.replace(/defs\.svg/g, defsPath);
				setSvgContent(modifiedText);
			} catch (error) {
				console.error('Error fetching CSS:', error);
			}
		} catch (error) {
			console.error('Error fetching apartment miniature SVG:', error);
		}
	};

	const availabilityLabel = (apartment) => {
		switch (apartment.availability) {
			case SOLD:
				return <span className="state-sold">{t('sold')}</span>;
			case RESERVED:
				return <span className="state-reserved">{t('reserved')}</span>;
			case AVAILABLE:
				return <span className="state-available">{t('available')}</span>;
			case UNKNOWN:
				return <span>{t('no_data')}</span>;
			default:
				return <span>{t('no_data')}</span>;
		}
	};

	const displayPrice = (number) => {
		const priceValue = parseFloat(number);
		const isApartmentSold = apartmentData.availability === SOLD;
		const formattedPrice = isApartmentSold
			? '-'
			: !isNaN(priceValue)
			? priceValue.toLocaleString('pl-PL', {
					style: 'currency',
					currency: 'PLN',
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
			  })
			: t('no_data');
		return formattedPrice;
	};

	useEffect(() => {
		fetchSvgContent();
	}, []);

	return (
		<div className={styles.apartment_tile}>
			<div className={styles.apartment_tile_favicon_container}>
				<button
					className={`${styles.plan_button} ${styles.plan_button_small} ${
						isCurrentUuidInFavorites ? styles.plan_button_selected : ''
					}`}
					onClick={(e) => {
						e.stopPropagation();
						toggleFavoriteApartmentHandler(apartmentData.uuid);
					}}
					onMouseEnter={() => setIsHeartHovered(true)}
					onMouseLeave={() => setIsHeartHovered(false)}
				>
					{isHeartHovered && isCurrentUuidInFavorites && (
						<IconClose
							title="usuń z ulubionych"
							className={styles.plan_button_icon_small}
						/>
					)}
					{!isHeartHovered && (
						<IconHeart
							title={
								isCurrentUuidInFavorites
									? 'usuń z ulubionych'
									: 'dodaj do ulubionych'
							}
							className={styles.plan_button_icon_small}
						/>
					)}
				</button>
			</div>
			{svgContent ? (
				<div
					ref={svgRef}
					className={styles.tile_svg_miniature}
					dangerouslySetInnerHTML={{ __html: svgContent }}
				/>
			) : (
				<div className={styles.svg_miniatures}>
					<Slab size="small" color={`#d3d3d3`} text={t('loading')} />
				</div>
			)}
			<div className={styles.apartment_tile_availability_container}>
				<div className={styles.apartment_tile_data_element}>
					<span className={styles.apartment_tile_data_value}>
						{availabilityLabel(apartmentData)}
					</span>
				</div>
			</div>
			<div className={styles.apartment_tile_data_container}>
				{isMultipleEdifice && (
					<div className={styles.apartment_tile_data_element}>
						<span className={styles.apartment_tile_data_type}>
							{t('building')}
						</span>
						<span className={styles.apartment_tile_data_value}>
							{apartmentData.edifice_name}
						</span>
					</div>
				)}
			</div>
			<div className={styles.apartment_tile_data_container}>
				<div className={styles.apartment_tile_data_element}>
					<span className={styles.apartment_tile_data_type}>{t('tier')}</span>
					<span className={styles.apartment_tile_data_value}>
						{apartmentTierName}
					</span>
				</div>
				<div className={styles.apartment_tile_data_element}>
					<span className={styles.apartment_tile_data_type}>{t('rooms')}</span>
					<span className={styles.apartment_tile_data_value}>
						{apartmentData.rooms}
					</span>
				</div>
				<div className={styles.apartment_tile_data_element}>
					<span className={styles.apartment_tile_data_type}>{t('area')}</span>
					<span className={styles.apartment_tile_data_value}>
						{apartmentData.area_original} m<sup>2</sup>
					</span>
				</div>
			</div>
			{showPrice && (
				<div className={styles.apartment_tile_price_container}>
					<div className={styles.apartment_tile_data_element}>
						<span className={styles.apartment_tile_data_type}>
							{t('price')}
						</span>
						{apartmentData.availability === SOLD ? (
							<span className={styles.apartment_tile_data_value}>
								{displayPrice(apartmentData.price_brutto)}
							</span>
						) : (
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '2rem',
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										alignItems: 'flex-start',
										// gap: '0.25rem',
										marginTop: '0.5rem',
									}}
								>
									{apartmentData.price_history?.length > 1 && (
										<span
											style={{
												fontSize: '0.75rem',
												textDecoration: 'line-through',
												color: 'var(--color-label)',
											}}
										>
											{parseFloat(
												apartmentData.price_history[0].price_brutto
											).toLocaleString('pl-PL', {
												style: 'currency',
												currency: 'PLN',
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})}
										</span>
									)}
									<span className={styles.apartment_tile_data_value}>
										{' '}
										{parseFloat(
											apartmentData.price_brutto
										).toLocaleString('pl-PL', {
											style: 'currency',
											currency: 'PLN',
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</span>
								</div>
								{apartmentData.price_offer?.on_offer && (
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<IconSale
											style={{
												color: 'var(--color-secondary)',
												width: '2.5rem',
												height: '2.5rem',
											}}
										/>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			)}
			<div className={styles.apartment_tile_button_container}>
				<button
					className={`${styles.planButton__cta} ${styles.tile_button_wide}`}
					onClick={() => setActiveApartmentHandle(apartmentData.id_original)}
				>
					{`Otwórz ${apartmentData.id_original}`}
				</button>
			</div>
		</div>
	);
}

export default ApartmentTile;
